// ============================================================
// MIDAS Chat Bot -- TypeScript Models
// ============================================================

// ME-MACHINEID-RENDER SP-2/SP-3: the `machine_card` / `machine_evidence` data
// cards use the LOCKED ME-CONTRACT nested shapes. They are the CANONICAL
// artifact-data shapes (the obsolete flat `MachineCardArtifactData` that used to
// live in THIS file was retired to
// `./_dead-code/flatMachineCardArtifactData.ts`). Type-only import from a leaf
// module (machineEvidenceModels imports nothing from chatBot) — no import cycle.
import type {
  MachineCardArtifactData,
  MachineEvidenceArtifactData,
} from "./machineEvidenceModels";

export type {
  MachineCardArtifactData,
  MachineEvidenceArtifactData,
} from "./machineEvidenceModels";

/** Role of a message sender */
export type MessageRole = "user" | "assistant";

// ============================================================
// MODEL-PICKER wire contract (MODEL_PICKER_SPEC.md §4A point 2 + §14.1 + §15.1)
// ============================================================
// The bot allowlist, VERBATIM, case-sensitive, no whitespace. These ten
// strings are the ONLY values that may ever be emitted as `model_override`
// on POST /api/v1/chat/stream — never free text, never user-editable, never
// concatenated. An unknown STRING silently falls back to the default model
// (HTTP 200, no error, no model echo) → mislabeled-answer risk, hence the
// closed union. Defined HERE (pure model module, no imports) so ChatBotApi,
// chatBotSlice, and midasV3Slice can all share the constants without an
// import cycle. UI display names/descriptions are i18n; ids are constants.
// v1.1 (MODEL-PICKER-11, bot `ae7d67b`): + Opus 4.8, Opus 4.8 (thinking),
// Haiku 4.5. Haiku is plain-only; Opus has both variants — no coupling
// logic, the dropdown simply lists the ids.
// v1.2 (MODEL-PICKER-12, bot `e0edad7`): + 4 OpenAI ids (gpt-5-mini, gpt-5,
// gpt-5-thinking, gpt-4o). Picking a gpt-* id sends the turn to OpenAI —
// disclosed in the picker UI (§15.3). gpt-5-thinking joins the thinking set;
// the other three are stats-only picks.
// MODEL-PICKER-OSS (bot `dc9367f`, 2026-07-06): + oss-qwen3.6-27b — our own
// open model (Qwen3.6-27B-FP8 on vLLM) on a RunPod-hosted endpoint, a THIRD
// provider distinct from Anthropic AND OpenAI (own egress caption + OSS
// group). Stats-only (NO `event: thinking`; `backend_served: "oss"`). The
// pod is parked when idle — a pick while it's down surfaces exactly ONE
// sanitized `event: error` and no answer (tolerated by the error-only
// stream path; see ChatMessageListV3 error block).
// MODEL-PICKER-OSS-48G (2026-07-06): + oss-qwen3.6-27b-48g — the SAME open
// model served on a smaller 48GB-GPU RunPod pod, ALONGSIDE the 96GB option
// (GPU-size A/B on `.cc`). Behavior IDENTICAL to oss-qwen3.6-27b: stats-only
// (NOT in THINKING_MODEL_IDS), same `backend_served: "oss"`, same parked-pod
// single-`event: error` tolerance, same OSS egress caption. A pick before
// the bot side + 48GB pod are live is silently ignored (default model
// answers — drop-and-continue posture per contract).
// MODEL-VERSION-CURRENCY-1 (2026-09-03, bot ticket MODEL-VERSION-CURRENCY-1;
// MACHINEID_TODO.md "[Request] Model-picker GUI: allowlist changes upcoming"
// + its Addendum, owner-attested that the bot side had ALREADY cut over):
// the allowlist drops from 12 ids to 10 and the DEFAULT moves.
//   - DEFAULT_MODEL_ID: claude-sonnet-4-6 -> claude-sonnet-5.
//   - + claude-opus-5: NEW, Claude backend, PLAIN only. The bot entry lists
//     no thinking variant for it, so it is deliberately NOT in
//     THINKING_MODEL_IDS (90s stream abort, no `event: thinking` expected).
//   - claude-sonnet-4-6 is RETAINED, labelled "previous default" in the UI.
//   - REMOVED: claude-opus-4-8, claude-opus-4-8-thinking,
//     claude-sonnet-5-thinking. gpt-5-thinking is now the ONLY thinking id.
// The bot is EXACT-MATCH and SILENTLY degrades a removed id to the default
// (HTTP 200, answered by claude-sonnet-5, no error frame, no model echo) --
// so a leftover dropdown option would LOOK like it worked while actually
// serving Sonnet 5. No dead options may remain anywhere.
// Stale persisted picks are retired by the SAME MODEL_OVERRIDE_IDS guards
// that already retire a stale pipeline "v3": midasV3Slice.readModelMap()
// drops any hydrated [threadKey, id] pair whose id is not in this array, and
// chatBotSlice re-validates the resolved id at dispatch time -- so a
// localStorage `claude-opus-4-8` resolves to DEFAULT_MODEL_ID in the picker,
// is never rendered as a label, and never reaches the wire.
// NOTE the default flip is a real behavior change for existing threads that
// rode the IMPLICIT default: they have no stored entry, so they now run
// claude-sonnet-5 (matching the bot's own default flip), not Sonnet 4.6.

export type ModelOverrideId =
  | "claude-sonnet-5"
  | "claude-sonnet-4-6"
  | "claude-opus-5"
  | "claude-haiku-4-5"
  | "gpt-5-mini"
  | "gpt-5"
  | "gpt-5-thinking"
  | "gpt-4o"
  | "oss-qwen3.6-27b"
  | "oss-qwen3.6-27b-48g";

/** Default/production model. Default = send NO `model_override` at all.
 *  MODEL-VERSION-CURRENCY-1: claude-sonnet-4-6 -> claude-sonnet-5. */
export const DEFAULT_MODEL_ID: ModelOverrideId = "claude-sonnet-5";

/** The 10 allowlist ids in the bot's own menu order
 *  (MODEL-VERSION-CURRENCY-1): 4 Anthropic, then 4 OpenAI, then 2 OSS.
 *  ModelPickerV3 slices its provider groups out of this same order, so the
 *  order is load-bearing -- keep it in sync with that component's
 *  ANTHROPIC_COUNT / OPENAI_COUNT. */
export const MODEL_OVERRIDE_IDS: readonly ModelOverrideId[] = [
  "claude-sonnet-5",
  "claude-sonnet-4-6",
  "claude-opus-5",
  "claude-haiku-4-5",
  "gpt-5-mini",
  "gpt-5",
  "gpt-5-thinking",
  "gpt-4o",
  "oss-qwen3.6-27b",
  "oss-qwen3.6-27b-48g",
] as const;

// ============================================================
// MODEL-PICKER-PIPELINE wire contract (API_CONTRACTS.md `pipeline_override`,
// added 2026-07-06 bot `97126fd`; "v3" added 2026-07-08 — agentic diagnosis;
// INVERTED 2026-07-15: v3 is the bot-side DEFAULT, v1/v2 are explicit opt-outs)
// ============================================================
// Optional sibling of `model_override` on POST /api/v1/chat/stream (JSON
// body AND multipart form; the sync POST /api/v1/chat IGNORES it — never
// sent there). The bot flipped its server default so an OMITTED
// `pipeline_override` runs v3 (agentic diagnosis) — so the UI-selectable
// values this client emits are now the OPT-OUTS: "v1" (classic single-shot)
// and "v2" (pipeline v2). "v1" is a contract-valid explicit value
// (bot-confirmed: byte-identical client rules to the old v1/v2 toggle).
// "v3" is NO LONGER emitted — nothing selected = field ABSENT = v3 default.
// Nothing selected = the field is ABSENT from the wire — never "".
// Per-message, no server stickiness: re-stamped on EVERY outgoing turn of a
// thread/column with an option selected, exactly the same client-side rules
// as `model_override`. Unknown/invalid values are silently treated as absent
// server-side (HTTP 200, default pipeline, no error frame) — nothing to
// handle client-side. A STALE persisted "v3" (from the pre-flip live build)
// fails the PIPELINE_OVERRIDE_IDS guards below → treated as no-selection so
// old localStorage can't keep sending explicit v3.
// NOTE the naming collision: the bot's pipeline "v1"/"v2"/"v3" is
// BOT-INTERNAL versioning, unrelated to our V1/V2/V3 chat surfaces.

export type PipelineOverrideId = "v1" | "v2";
export const PIPELINE_OVERRIDE_V1: PipelineOverrideId = "v1";
export const PIPELINE_OVERRIDE_V2: PipelineOverrideId = "v2";

/** All legal UI-selectable `pipeline_override` wire values, in menu order.
 *  Used to validate the per-thread selection at dispatch time (chatBotSlice)
 *  and on localStorage hydration (midasV3Slice) — mirrors MODEL_OVERRIDE_IDS.
 *  Adding a future pipeline id here is the ONLY place the send path needs to
 *  learn about it (the resolver validates against this set, not a hard-coded
 *  value). "v3" is deliberately NOT in this set — it is the bot default when
 *  the field is omitted, and the same guard retires stale persisted "v3". */
export const PIPELINE_OVERRIDE_IDS: readonly PipelineOverrideId[] = [
  "v1",
  "v2",
] as const;

/** Extended-reasoning model ids (spec §14.1 + §15.1). An EXPLICIT membership
 *  set — deliberately NOT a `-thinking` suffix test, because a future id could
 *  break that naming convention silently. Thinking picks get the 180s
 *  client stream abort and are expected to emit `event: thinking` frames.
 *  MODEL-VERSION-CURRENCY-1: exactly ONE member now -- GPT-5 (thinking).
 *  Both Claude thinking ids left the bot allowlist and the new
 *  claude-opus-5 is plain-only, so every Claude pick is a 90s stats-only
 *  turn. Kept as a Set (not collapsed to an equality check) so a future
 *  thinking id stays a one-line addition. */
export const THINKING_MODEL_IDS: ReadonlySet<string> = new Set<ModelOverrideId>([
  "gpt-5-thinking",
]);

/** Predicate used EVERYWHERE thinking-specific behavior keys (stream
 *  timeout, thinking-panel expectation). Membership test, not suffix test. */
export const isThinkingModel = (id: string | null | undefined): boolean =>
  !!id && THINKING_MODEL_IDS.has(id);

/** Per-turn stats frame (`event: stats`, spec §14.3) — bot `865e9e0`.
 *  Exactly ONE per honored pick, emitted after `metadata` and before `done`.
 *  13 fields, VERBATIM names — stored on the message untouched (no renames).
 *  All optional defensively; a hard-error turn emits NO stats at all and an
 *  un-honored pick (flag off / unknown id) emits none either — absence is
 *  normal, not an error. A REFUSAL turn DOES carry stats (`refusal: true`). */
export interface ModelTurnStats {
  timestamp_utc?: string;
  thread_id?: string;
  model?: string;
  thinking?: boolean;
  model_override?: string | null;
  time_to_first_token_ms?: number;
  total_latency_ms?: number;
  input_tokens?: number;
  output_tokens?: number;
  est_cost_usd?: number;
  citation_tag_count?: number;
  truncated?: boolean;
  refusal?: boolean;
  /** v1.2 (§15.4) + MODEL-PICKER-OSS: which backend served the turn —
   *  `"claude" | "claude_pool" | "openai" | "oss" | null` per contract, but
   *  typed OPEN + OPTIONAL on purpose: tolerate absence (older frames,
   *  pre-restart bot) AND unknown future values (no allowlist crash, no
   *  console spam — an unknown non-empty value renders as a raw muted tag
   *  in the stats row; `"oss"` renders as "via RunPod (OSS)"). */
  backend_served?: string;
}

/** A source document referenced by a bot response */
export interface ChatSource {
  document: string;
  page?: number;
  pageFrom?: number;
  pageTo?: number;
  url?: string;
  imageUrl?: string;
  /** R5 sub-task 1: secondary lookup key when bot omits `page`.
   *  Bot may emit a sheet/document name (e.g. "Vstupný modul 1") alongside
   *  imageUrl when no discrete page integer is available. The artifact-side
   *  resolver normalises this (lowercased + trimmed) and matches against
   *  artifact.title / data.name / data.page_name. Optional. */
  name?: string;
}

/** A service report cited by the bot (MC-22) */
export interface SrCitation {
  /** The SR number in canonical form, e.g. "SR-260326-002" */
  reportNumber: string;
  /** How many chunks in the retrieved context referenced this SR (>=1) */
  citationCount: number;
}

/** A single message in the conversation */
export interface ChatMessage {
  id: string;
  /** R5 sub-task 5a: bot-minted message_id from the SSE `answer` (or `done`)
   *  event's `answer_data.message_id`. Stable identifier for the assistant
   *  reply — used by Like/Dislike feedback POSTs to `/v1/bot/midas/feedback`.
   *  Optional because older bot versions do not emit it; the frontend gates
   *  the POST behind its presence and logs a one-time warning when absent. */
  botMessageId?: string;
  role: MessageRole;
  text: string;
  timestamp: string;
  sources?: ChatSource[];
  suggestions?: string[];
  /** True if the message failed to send (user messages only) */
  sendFailed?: boolean;
  /** Bot response intent — PLATFORM_* prefix means platform mode, no prefix means machine mode */
  intent?: string;
  /** Artifacts attached by the bot (PDF page images, platform files, etc.) — M2+ */
  artifacts?: ChatArtifact[];
  /** Blob URL of an image attached by the user (M7-A: image upload) */
  imageUrl?: string;
  /** Original filename of the user-attached image */
  imageName?: string;
  /** Action buttons attached by the bot (M7-D: action buttons) */
  actions?: ChatAction[];
  /** ID of the action that was clicked (once set, the action group is read-only) */
  clickedActionId?: string;
  /** Action request data for the MIDAS Action Framework (MA-0) */
  actionRequest?: ChatActionRequest;
  /** Tier-0 action requests rendered as chips (nav.switch_machine, nav.switch_platform, etc.) */
  uiActionRequests?: ChatActionRequest[];
  /** Service report citations — NEW (MC-22). Each entry renders as a clickable chip. */
  srCitations?: SrCitation[];
  /** MODEL-PICKER-11 (§14.2): accumulated `event: thinking` deltas for this
   *  turn (thinking picks only). Attached at stream completion so the panel
   *  stays re-expandable while the turn is on screen (the per-thread buffer's
   *  live copy clears with streamingText). Absent on non-thinking turns. */
  thinkingText?: string;
  /** MODEL-PICKER-11 (§14.3): the full 13-field `event: stats` frame,
   *  VERBATIM field names, attached to the completed bot message. Absent on
   *  hard-error turns and un-honored picks — tolerate absence. */
  modelStats?: ModelTurnStats;
}

/** Action request embedded in a chat message (MA-0: Action Framework) */
export interface ChatActionRequest {
  action_id: string;
  action_type: string;
  schema: {
    fields: ChatActionSchemaField[];
  };
  values: Record<string, any>;
  display: {
    title: string;
    summary?: string;
    icon?: string;
  };
  approval_tier: number;
  /** Localized display label from bot (e.g. machine name for nav, "Create Service Report" for SR) */
  label?: string;
  /** Optional rendering hint from bot: "chip", "form_card", "modal". Supplements approval_tier. */
  render_hint?: string;
}

/** Schema field definition within an action request */
export interface ChatActionSchemaField {
  name: string;
  type: "text" | "textarea" | "dropdown" | "file" | "date" | "number" | "currency" | "readonly";
  label: string;
  description?: string;
  required?: boolean;
  max_length?: number;
  min?: number;
  max?: number;
  options?: Array<{ value: string; label: string }>;
  accept?: string[];
  max_size?: number;
  display_hints?: {
    emphasis?: "normal" | "high" | "critical";
    kind?: string;
    group?: string;
    help_text?: string;
    dependency?: { field: string; value: string };
  };
}

/** Supported artifact types */
export type ChatArtifactType =
  | "image"
  | "platform_file"
  | "table"
  | "machine_card"
  /** ME-MACHINEID-RENDER SP-3: question-focused evidence card (hero + supporting[]
   *  + deep-link). Registered so `mapBotArtifacts` no longer silently drops it;
   *  emit gate MACHINE_EVIDENCE_RENDER_LIVE is OFF so nothing renders live yet. */
  | "machine_evidence"
  | "sr_summary"
  | "diagram_overlay"
  /** MIDAS-BUG-001-P2: user-uploaded image attached to a chat message */
  | "user_image";

/** Data payload for type: "user_image" artifacts (MIDAS-BUG-001-P2) */
export interface UserImageArtifactData {
  mime: string;
  size: number;
  filename: string;
  width?: number;
  height?: number;
}

/** An artifact attached to a bot message (parsed from SSE answer event).
 *  MC-25: `source` may also be "cross_reference" for list-ref artifacts. */
export interface ChatArtifact {
  type: ChatArtifactType;
  title: string;
  /** "bot" and "machineid" are legacy; "user" = user-uploaded image (P2);
   *  "cross_reference" = MC-25 cross-reference artifact paired with <list-ref> chips. */
  source: "bot" | "machineid" | "user" | "cross_reference";
  url: string;
  document_id?: string;
  page?: number;
  mime_type?: string;
  entity_type?: string;
  entity_id?: string;
  file_id?: string;
  /** Structured data payload for rich artifacts (table, machine_card,
   *  machine_evidence, sr_summary, user_image). `machine_card` /
   *  `machine_evidence` carry the LOCKED ME-CONTRACT nested shapes (SP-2/SP-3). */
  data?:
    | TableArtifactData
    | MachineCardArtifactData
    | MachineEvidenceArtifactData
    | SRSummaryArtifactData
    | DiagramOverlayArtifactData
    | UserImageArtifactData;
}

// ============================================================
// Rich Artifact Data Shapes (M3)
// ============================================================

/** Column definition for a table artifact */
export interface TableArtifactColumn {
  key: string;
  label: string;
  width?: string;
}

/** A single row in a table artifact */
export interface TableArtifactRow {
  [key: string]: string | number | null | undefined;
  link?: string;
}

/** Data payload for type: "table" artifacts */
export interface TableArtifactData {
  columns: TableArtifactColumn[];
  rows: TableArtifactRow[];
  total_count?: number;
}

// NOTE (ME-MACHINEID-RENDER SP-2): the obsolete FLAT `MachineCardArtifactData`
// interface that used to live here was RETIRED to
// `./_dead-code/flatMachineCardArtifactData.ts` (renamed
// `FlatMachineCardArtifactData`). The canonical `machine_card` shape is now the
// nested ME-CONTRACT `MachineCardArtifactData` re-exported at the top of this
// file from `./machineEvidenceModels`.

/** Timeline event in an SR summary artifact */
export interface SRTimelineEvent {
  event: string;
  timestamp: string;
  user?: string;
}

/** Data payload for type: "sr_summary" artifacts */
export interface SRSummaryArtifactData {
  id: string;
  number: string;
  title: string;
  description?: string;
  status: string;
  priority?: string;
  machine_model?: string;
  created_at: string;
  offers_count?: number;
  assigned_provider?: string;
  timeline?: SRTimelineEvent[];
  link: string;
}

/** A single hotspot on a diagram page */
export interface DiagramHotspot {
  vi_id: string;
  label: string;
  description?: string;
  x_pct: number;
  y_pct: number;
  w_pct: number;
  h_pct: number;
  type?: string;
  component_id?: string;
  confidence?: number;
  position_source?: string;
  /**
   * OPTIONAL (since 2026-08-21): "#rrggbb" lowercase hex from the bot-side
   * canonical component-type palette. Present iff the type has a canonical
   * entry — absent means the frontend type -> color map decides.
   * NEVER assume presence.
   */
  color?: string;
}

/** Wiring connection between two components on a diagram page */
export interface DiagramWiringConnection {
  from_component: string;
  from_terminal: string;
  to_component: string;
  to_terminal: string;
  wire_label: string;
}

/** Cross-reference from a component to another page/sheet */
export interface DiagramCrossReference {
  from_component: string;
  target_page: number;
  target_sheet: string;
  reference_text: string;
}

/** Wire label entry on a diagram page */
export interface DiagramWireLabel {
  label: string;
  from_component: string;
  to_component: string;
  cross_section: string;
  color: string;
}

/** Response from GET /v1/bot/diagram/{page}/hotspots */
export interface DiagramHotspotsResponse {
  page_number: number;
  page_id?: string;
  page_title?: string;
  image_url: string;
  hotspots: DiagramHotspot[];
  count: number;
  wiring_connections?: DiagramWiringConnection[];
  cross_references?: DiagramCrossReference[];
  wire_labels?: DiagramWireLabel[];
}

/** A single page entry from the page-list endpoint (Phase 2) */
export interface DiagramPage {
  page_number: number;
  page_id: string;
  page_title: string;
  image_url: string;
  component_count: number;
}

/** Response from GET /v1/bot/diagram/pages (Phase 2) */
export interface DiagramPageListResponse {
  pages: DiagramPage[];
  total_count: number;
}

/** A single search result from the diagram search endpoint (Phase 2) */
export interface DiagramSearchResult {
  component_id: string;
  label: string;
  type: string;
  page_number: number;
  page_title: string;
  confidence: number;
}

/** Response from GET /v1/bot/diagram/search (Phase 2) */
export interface DiagramSearchResponse {
  results: DiagramSearchResult[];
  query: string;
  total_count: number;
}

/** Response from GET /v1/bot/diagram/component/{id} (Phase 2) */
export interface DiagramComponentDetailResponse {
  component_id: string;
  label: string;
  type: string;
  description?: string;
  pages: number[];
  wiring_connections: DiagramWiringConnection[];
  cross_references: DiagramCrossReference[];
}

/** Data payload for type: "diagram_overlay" artifacts.
 *  MC-25/MC-26: extended with optional list metadata + localised info_prompt.
 *  All new fields are optional; artifacts without them degrade gracefully. */
export interface DiagramOverlayArtifactData {
  page: number;
  components: string[];
  image_url: string;
  hotspots_url: string;
  /** MC-25: supplementary display/debug ID (e.g. "050"). NOT the join key — `page` is. */
  list_id?: string;
  /** MC-27: Qdrant collection ID the artifact was retrieved from.
   *  Passed back to the bot in `page_info_request.collection` on (i) clicks
   *  so the bot can skip intent classification + vector search.
   *  Valid values: sbl500_manual_toc_v2 | sbl500_electrical_toc_v2 | SINUMERIK_toc_v2.
   *  If absent on older artifacts, callers fall back to sbl500_electrical_toc_v2
   *  (all electrical schema artifacts come from that collection). */
  collection?: string;
  /** Legacy / generic name field. */
  page_name?: string;
  /** Locale-specific page name (default / Slovak) used by the (i) friendly-text constructor. */
  name?: string;
  /** English page name. */
  name_en?: string;
  /** Czech page name. */
  name_cs?: string;
  /** MC-26: localised info prompt sent as the stream body when the user clicks the (i) button.
   *  NOT displayed in the chat bubble — a friendly short message is constructed instead. */
  info_prompt?: {
    sk?: string;
    en?: string;
    cs?: string;
  };
}

/** An action button attached to a bot message */
export interface ChatAction {
  id: string;
  label: string;
  style: "primary" | "secondary" | "text";
  context?: Record<string, unknown>;
}

/** Machine context set via "Chat about this machine" button */
export interface MachineContext {
  machineId: string;
  model: string;
  serialNumber: string;
  // Display fields (optional — may not always be available from API)
  internalNumber?: string;
  system?: string;
  manufacturerName?: string;
  typeName?: string;
  axisConfigName?: string;
  controlSystemBrandName?: string;
  imageContent?: string; // base64 image content
  description?: string;
  productionYear?: number;
}

// ============================================================
// API Request / Response Types
// ============================================================

/** Result of an executed action, posted back to bot for conversation continuity (MA-0b.3) */
export interface ActionResultPayload {
  action_id: string;
  action_type: string;
  success: boolean;
  result?: Record<string, any>;
  reason?: string;
}

/** POST /v1/bot/chat -- request body */
export interface SendMessageRequest {
  message: string;
  machine_id?: string;
  language?: string;
  /** MA-0b.3: Action result posted back to bot after frontend execution */
  action_result?: ActionResultPayload;
}

/** POST /v1/bot/chat -- response body */
export interface SendMessageResponse {
  message: string;
  sources?: ChatSource[];
  suggestions?: string[];
  /** Service report citations — NEW (MC-22). Optional; absent on older bot versions. */
  srCitations?: SrCitation[];
  metadata?: {
    processingTime?: number;
    model?: string;
    intent?: string;
  };
}

/** GET /v1/bot/chat/history -- response body (snake_case from bot) */
export interface ChatHistoryResponse {
  messages: ChatHistoryMessage[];
  session_start?: string;
  machine_context?: {
    machine_id: string;
    model: string;
    serial_number: string;
  } | null;
}

/** A single message from the history endpoint */
export interface ChatHistoryMessage {
  role: MessageRole;
  text: string;
  timestamp: string;
  sources?: ChatSource[];
}

/** POST /v1/bot/chat/context -- request body (snake_case for Python bot gateway) */
export interface SetContextRequest {
  machine_id: string | null;
}

/** POST /v1/bot/chat/context -- response body (machine mode entry, snake_case from bot) */
export interface SetContextResponse {
  machine: {
    machine_id: string;
    model: string;
    serial_number: string;
  };
  confirmation_message: string;
}

/** POST /v1/bot/chat/context -- response body (machine mode exit, machineId=null) */
export interface ClearContextResponse {
  mode: string;
  message: string;
}

/** GET /v1/customer/settings/bot-chat -- response body */
export interface BotAccessResponse {
  enabled: boolean;
  globalEnabled: boolean;
}

/** GET /v1/bot/health -- response body */
export interface HealthCheckResponse {
  status: string;
  botVersion?: string;
}

/** SSE event types received during streaming.
 *  MODEL-PICKER-11: + "thinking" (summarized-reasoning deltas, thinking picks
 *  only) and "stats" (one per honored pick, after `metadata`, before `done`). */
export type SSEEventType = "status" | "answer" | "token" | "thread_created" | "action_request" | "done" | "error" | "thinking" | "stats";

/** SSE event payload */
export interface SSEEvent {
  event: SSEEventType;
  data: string;
}

// ============================================================
// Thread-Scoped Streaming (MIDAS-SCOPESTREAM)
// ============================================================

/** Sentinel thread ID used by the popup chatbot (no real threads). */
export const POPUP_THREAD_ID = "popup";

/** V3-SEGMENT-STREAMING (SP-2): the transient "receipt" row for the in-flight
 *  agentic-diagnosis round, driven by `loop_progress` frames. NEVER persisted —
 *  the completed/reloaded message renders marker-derived rows instead (frame
 *  rows are stream-transient; the final render is always marker-derived, which
 *  is what makes reload/export mirror the live view). */
export interface LiveRowState {
  /** Accumulated `stage` display labels (`live_label`), joined with " · ". */
  labels: string[];
  /** True once a `step_end` / `finalizing` frame settled the row (no more
   *  animation). Provisional — cleared/superseded when the round's marker line
   *  arrives in the token stream (marker wins; see the dedupe rule). */
  settled: boolean;
  /** Final settled row text (from `step_end.label` / `finalizing.label`). */
  settledLabel?: string;
  /** Marker-line count in `streamingText` when this row opened. Once the count
   *  grows (a NEW marker line completed) the inline marker row supersedes this
   *  frame row for the round → the frame row is dropped (dedupe: never render
   *  both a settled frame-row AND a marker-row for the same round). */
  markerBaseline: number;
}

/** Per-thread streaming buffer. Stores only streaming-related fields
 *  plus a single pendingBotMessage for background-completing streams.
 *  Messages continue to live in state.messages (the global array). */
export interface ThreadBuffer {
  /** The live streaming text being built up token by token. */
  streamingText: string | null;
  /** MODEL-PICKER-11 (§14.2): live `event: thinking` deltas for the in-flight
   *  turn (thinking picks only). Per-thread by construction → works in attach
   *  columns for free. Cleared wherever streamingText clears; the completed
   *  message carries its own copy (ChatMessage.thinkingText). */
  thinkingText: string | null;
  /** Whether this thread's stream is in flight. */
  isLoading: boolean;
  /** Progress step text ("Searching documents...", "Analyzing..."). */
  currentStep: string | null;
  /** Error message if the stream failed. */
  error: string | null;
  /** When a stream completes while the user is on a DIFFERENT thread,
   *  the final bot message is stored here. On next loadThread for this
   *  thread, it gets appended to the API-fetched messages. */
  pendingBotMessage: ChatMessage | null;
  /** Timestamp of last activity (for GC eviction ordering). */
  lastActivity: number;
  /** V3-SEGMENT-STREAMING (SP-2): the live/settled `loop_progress` receipt row
   *  for the in-flight round. Transient (never persisted). */
  liveRow: LiveRowState | null;
  /** V3-SEGMENT-STREAMING (SP-2): set true by the first `loop_progress` frame on
   *  this stream → the transient `searching` status line is suppressed
   *  thereafter (contract §10.1.6: `loop_progress` supersedes the fabricated
   *  `status` frame). Non-v3 turns never get a frame → stays false → unchanged. */
  sawLoopProgress: boolean;
}

// ============================================================
// Redux State Shape
// ============================================================

/** Action state tracked per action_id (MA-0: Action Framework) */
export interface ChatActionStatus {
  actionId: string;
  actionType: string;
  state: "pending" | "executing" | "success" | "error";
  result?: {
    success: boolean;
    entityId?: string;
    entityNumber?: string;
    message: string;
    link?: string;
    data?: Record<string, any>;
  };
  error?: string;
  createdAt: number;
}

export interface ChatBotState {
  /** Whether the chat panel overlay is visible */
  isOpen: boolean;
  /** Full conversation messages */
  messages: ChatMessage[];
  /** Waiting for bot response */
  isLoading: boolean;
  /** Current SSE progress step text (i18n key) */
  currentStep: string | null;
  /** Currently selected machine context (or null) */
  machineContext: MachineContext | null;
  /** Whether machine context API call is in progress */
  machineContextLoading: boolean;
  /** Last health check result */
  isBotAvailable: boolean;
  /** Feature flag result (per-company + global) */
  isBotEnabled: boolean;
  /** Whether the checkBotAccess call has completed (true after fulfilled OR rejected) */
  botAccessChecked: boolean;
  /** Bot responded while panel was closed */
  hasUnread: boolean;
  /** Last error message */
  error: string | null;
  /** Whether the inline machine picker is shown (replaces Dialog) */
  showMachinePicker: boolean;
  /** Accumulated token text during SSE streaming (progressive rendering) */
  streamingText: string | null;

  /** Platform mode messages saved when entering machine mode */
  savedPlatformMessages: ChatMessage[];
  /** Machine mode messages saved per machine (keyed by machineId) */
  savedMachineMessages: Record<string, ChatMessage[]>;

  /** Action states tracked by action_id (MA-0: Action Framework) */
  pendingActions: Record<string, ChatActionStatus>;
  /** Set of executed action_ids to prevent duplicate execution */
  executedActionIds: string[];

  /** When true, next message will include new_thread: true in the request body.
   * Set by startNewChat, consumed (cleared) by sendMessage. */
  newThreadPending: boolean;

  /** Per-thread streaming buffers. Keyed by threadId (or temp optimistic ID for new threads).
   *  Does NOT store messages -- only streaming state + pendingBotMessage for background completions. */
  threadBuffers: Record<string, ThreadBuffer>;

  /** Maps each in-flight stream to its originating thread ID.
   *  Key = thunk requestId (from meta.requestId), Value = threadId.
   *  Supports multiple concurrent streams. */
  activeStreams: Record<string, string>;
}
